import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { VStack, HStack } from 'native-base';
import { Stack } from '@mui/material';
import {
  animated,
  useTransition,
  AnimatedProps,
  useSpringRef,
} from 'react-spring';

// Components
import {
  MemoAppDescriptionPanel,
  MemoAppDescriptionSubPanel,
  IconButton,
} from '../../';

// Assets
import { theme } from '@breakroom-nx/assets';
import {
  ArrowNarrowLeftIcon,
  ArrowNarrowRightIcon,
} from 'react-native-heroicons/solid';

const panels: ((
  props: AnimatedProps<{ style: CSSProperties }>
) => React.ReactElement)[] = [
  ({ style }) => (
    <animated.div style={{ ...style }}>
      <MemoAppDescriptionPanel />
    </animated.div>
  ),
  ({ style }) => (
    <animated.div style={{ ...style }}>
      <MemoAppDescriptionSubPanel
        header="Truly Global"
        subheader="Borders don't exist here"
        body="Create and join rooms all over the world! Talk to anyone, anywhere, about what interests you. Going abroad? Why not find a local and get the inside scoop beforehand?"
      />
    </animated.div>
  ),
  ({ style }) => (
    <animated.div style={{ ...style }}>
      <MemoAppDescriptionSubPanel
        header="Anxious?"
        subheader="Even introverts are welcome here"
        body="Create an anonymous account or toggle anonymous mode anytime with a registered account. Go explore without feeling anxious about diving into discussions."
      />
    </animated.div>
  ),
  ({ style }) => (
    <animated.div style={{ ...style }}>
      <MemoAppDescriptionSubPanel
        header="Here Today, Gone Tomorrow"
        subheader="Life is about living and making memories"
        body="If the activity in a room dies down, the room will eventually expire and disappear. But that is part of the fun. Feel free to start a new room or join another one where the party is rockin'"
      />
    </animated.div>
  ),
];

// import styles from './app-description-container.module.scss';

/* eslint-disable-next-line */
export interface AppDescriptionContainerProps {}

export const AppDescriptionContainer = (
  props: AppDescriptionContainerProps
) => {
  const [index, set] = useState(0);
  const [rightClick, setRightClick] = useState(true);
  const onClick = useCallback(
    (right: boolean) =>
      set((state) => (right ? state + 1 : state - 1) % panels.length),
    []
  );
  const transRef = useSpringRef();
  const transitions = useTransition(index, {
    ref: transRef,
    keys: null,
    from: {
      opacity: 0,
      transform: `translate3d(${rightClick ? '100%' : '-100%'},0,0)`,
    },
    enter: {
      opacity: 1,
      transform: 'translate3d(0%,0,0)',
      width: '100%',
    },
  });

  useEffect(() => {
    transRef.start();
  }, [index]);

  return (
    <VStack justifyContent="space-between">
      <HStack alignItems="center" justifyContent="center">
        {transitions((style, i) => {
          const Page = panels[i];
          return <Page style={style} />;
        })}
      </HStack>
      <Stack
        direction={'row-reverse'}
        justifyContent={'space-between'}
        alignItems={'flex-end'}
        paddingTop={5}
      >
        <IconButton
          disabled={index === panels.length - 1}
          onPress={() => {
            setRightClick(true);
            onClick(true);
          }}
          icon={
            <ArrowNarrowRightIcon color={theme.colors.primary[400]} size={20} />
          }
        />
        <IconButton
          disabled={index === 0}
          onPress={() => {
            setRightClick(false);
            onClick(false);
          }}
          icon={
            <ArrowNarrowLeftIcon color={theme.colors.primary[400]} size={20} />
          }
        />
      </Stack>
    </VStack>
  );
};
